import * as R from 'rambda';

import type {
  TDownloadPack,
  IProfileDocumentRead,
  IProfileDocumentWrite
} from '../types/profiles/documents';
import type { TGenericObject } from '../types/base';

// Returns the lower-cased file extension, or '' when the name has no '.'.
const getFileExtension = (fileName: string) =>
  fileName.includes('.') ? fileName.toLowerCase().split('.').pop() || '' : '';

export const prepareDocumentData = ({
  type,
  fileSize,
  formValues
}: {
  type: string;
  fileSize: number;
  formValues: TGenericObject;
}) => {
  const formData = R.pick(
    [
      'expiresAt',
      'displayName',
      'policyNumber',
      'lastUpdatedAt',
      'institutionName',
      'physicalStorageLocation'
    ],
    formValues
  );
  const documentData: Omit<
    IProfileDocumentWrite,
    'url' | 'fileName' | 'created' | 'updated'
  > = {
    type,
    fileSize,
    ...formData
  };

  return documentData;
};

export const toEditableDocument = (values?: IProfileDocumentRead) =>
  R.pick(
    [
      'displayName',
      'policyNumber',
      'institutionName',
      'physicalStorageLocation',
      'expiresAt',
      'lastUpdatedAt'
    ],
    values
  );

// Build the zip entry list from a set of uploaded documents. Only documents
// that actually have a file (`url`) are included, the name falls back to the
// original file name, the correct extension is appended when missing, and
// duplicate names are suffixed so JSZip doesn't silently overwrite entries.
export const buildDownloadPack = (
  documents: IProfileDocumentRead[] = []
): TDownloadPack => {
  const usedNames = new Set<string>();

  return documents
    .filter((doc) => Boolean(doc.url))
    .map((doc) => {
      const fileName = doc.fileName || '';
      const extension = getFileExtension(fileName);
      // displayName is free text — strip path separators so entries don't get
      // nested inside the zip.
      const base = (doc.displayName?.trim() || fileName || 'document').replace(
        /[/\\]/g,
        '-'
      );

      let name =
        extension && !base.toLowerCase().endsWith(`.${extension}`)
          ? `${base}.${extension}`
          : base;

      if (usedNames.has(name)) {
        const dot = name.lastIndexOf('.');
        let attempt = 1;
        let candidate = name;
        while (usedNames.has(candidate)) {
          candidate =
            dot > 0
              ? `${name.slice(0, dot)} (${attempt})${name.slice(dot)}`
              : `${name} (${attempt})`;
          attempt += 1;
        }
        name = candidate;
      }

      usedNames.add(name);
      return { url: doc.url, name };
    });
};
